/**
 * Cohort picker — a remote-search combobox over one client's cohorts.
 *
 * The sibling of `clients/client-picker.tsx`, built for the same reason
 * one step down the hierarchy. The cohort `<Select>`s it replaces all read
 * `GET /coach/admin/clients/{id}/cohorts`, which is UNPAGED — and could not
 * be bounded, because that list sorts soonest-start-first, so any ceiling
 * keeps the oldest (most likely ended) cohorts. Batch create filters what
 * it receives to cohorts that have not ended and would have reported "no
 * active cohorts" for a client that has them; a preselected cohort past
 * the boundary would have vanished from its own field.
 *
 * Searching remotely removes the premise. Three properties carry it:
 *
 * - **Selection is an immutable cohort id**, so a renamed cohort cannot
 *   change what a form points at.
 * - **The selected cohort is resolved BY ID** on its own query, so its
 *   label survives a pending search, a result page that excludes it, and
 *   a cold `?cohortId=` deep link.
 * - **`joinable` is a SERVER filter**, not a predicate over the fetched
 *   rows. Filtering in the browser can only narrow one page, which is
 *   precisely how "no active cohorts" became sayable about a client that
 *   had them.
 */
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { queryKeys } from '../api/query-keys'
import { todayJstIso } from '../common/access-window'
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  selectTriggerVariants,
} from '@speaking-coach/ui'
import {
  Listbox,
  ListboxMessage,
  ListboxOption,
  ListboxSearch,
  useListboxId,
} from '../components/ui/listbox'
import { cn } from '../components/ui/cn'
import { adminCohortClient, messageForCohortError } from './api'
import { cohortByIdParams, useCohort } from './use-cohort'
import type { CohortRow, ListCohortsParams } from './types'
import { cohortEnrollmentReadinessProblems } from './enrollment-readiness'

const SEARCH_DEBOUNCE_MS = 250
const SEARCH_LIMIT = 20

/** Addressed by value like any option, so it cannot collide with an id. */
const CLEAR_VALUE = '__no_cohort__'

interface CohortPickerProps {
  id: string
  /** Cohorts belong to one client; the picker is always scoped to it. */
  clientId: string
  value: string
  onChange: (cohortId: string) => void
  label: string
  /**
   * Text for the empty option — "No cohort" on a form where a cohort is
   * optional, "All cohorts" on a filter. Omitting it makes the choice
   * required.
   */
  emptyLabel?: string
  required?: boolean
  disabled?: boolean
  ariaInvalid?: boolean
  ariaDescribedby?: string
  size?: 'default' | 'sm'
  /**
   * Offer only cohorts a wave can still land in. A SERVER filter
   * (`joinable`), because narrowing the fetched page in the browser hides
   * live cohorts that sit past the search limit — the exact failure that
   * made this component necessary. The current selection is exempt: it is
   * resolved by id on a separate, unfiltered query, so a form pre-filled
   * with an ended cohort still shows it rather than rendering as unset.
   */
  joinableOnly?: boolean
  /**
   * Keep incomplete cohorts visible so the operator can see what exists, but
   * make them non-selectable and name the missing enrollment term.
   */
  requireCompleteEnrollmentDefaults?: boolean
  /**
   * What to show on the trigger while the SELECTION's own lookup is
   * pending or has failed — a name the caller already holds.
   *
   * The student edit page is the case: it knows `cohortName` from the
   * student row, and its old control held itself disabled until the whole
   * cohort list arrived rather than risk rendering "No cohort" over a real
   * membership. A failed lookup must not claim there is no cohort either,
   * so this covers both, and the field stays usable throughout.
   */
  fallbackLabel?: string
}

/** Name plus locked defaults needed to choose the right paid period. */
function describe(cohort: CohortRow): string {
  const starts = cohort.scheduledStartOn ?? 'start not set'
  const trophyGoal = cohort.defaultTrophyGoal ?? 'not set'
  return `${cohort.name} · ${starts} → ${cohort.accessEndsOn} · trophy goal ${trophyGoal}`
}

export function CohortPicker({
  id,
  clientId,
  value,
  onChange,
  label,
  emptyLabel,
  required = false,
  disabled = false,
  ariaInvalid,
  ariaDescribedby,
  size = 'default',
  joinableOnly = false,
  requireCompleteEnrollmentDefaults = false,
  fallbackLabel,
}: CohortPickerProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()
  const listId = useListboxId()

  useEffect(() => {
    const handle = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  const searchParams: ListCohortsParams = {
    clientId,
    search: search || undefined,
    joinable: joinableOnly ? true : undefined,
    limit: SEARCH_LIMIT,
    offset: 0,
  }
  const results = useQuery({
    queryKey: queryKeys.cohortList(searchParams),
    queryFn: () => adminCohortClient.listAll(searchParams),
    // Nothing until the popover opens, and nothing at all without a
    // client — a cohort field on a form whose client step is unanswered
    // has no question to ask yet.
    enabled: open && clientId !== '',
    // Previous data ONLY from the same client.
    //
    // `keepPreviousData` exists so the list does not flash on every
    // keystroke — but `clientId` is in this key too, so a change of CLIENT
    // is also "previous". Carrying it across meant reopening the picker
    // before the new client's request settled rendered the OLD client's
    // cohorts as clickable; choosing one handed a foreign id back to the
    // form, and the target-scoped write 404s. That is the same stale
    // cross-client control this component was built to remove, so it must
    // not be reintroduced by a caching convenience.
    //
    // Comparing the previous QUERY's key rather than filtering the rows:
    // the rows would still sit in the observer as `data`, and anything
    // else reading it (the cursor, the "showing N of M" line) would go on
    // believing them.
    placeholderData: (previous, previousQuery) => {
      const previousParams = previousQuery?.queryKey?.[2] as
        | ListCohortsParams
        | undefined
      return previousParams?.clientId === clientId ? previous : undefined
    },
  })

  const selected = useCohort(clientId, value)
  const rows = results.data?.cohorts ?? []
  const total = results.data?.total ?? 0
  const readinessProblems = (cohort: CohortRow): string[] =>
    requireCompleteEnrollmentDefaults
      ? cohortEnrollmentReadinessProblems(cohort, todayJstIso())
      : []

  // Where the cursor sits when server rows land — the first match, or the
  // clear row when there is nothing to point at. Stated once here, for the
  // reason written out in `clients/client-picker.tsx`: Enter acts on the
  // highlighted row, and rows arrive after the first render.
  const [active, setActive] = useState('')
  const selectableRows = rows.filter((row) => readinessProblems(row).length === 0)
  const firstMatchId = selectableRows[0]?.cohortId ?? ''
  const clearable = emptyLabel !== undefined
  useEffect(() => {
    setActive(firstMatchId || (clearable ? CLEAR_VALUE : ''))
  }, [firstMatchId, clearable])

  function choose(cohort: CohortRow | null): void {
    if (cohort !== null) {
      queryClient.setQueryData(
        queryKeys.cohortList(cohortByIdParams(clientId, cohort.cohortId)),
        { cohorts: [cohort], total: 1 },
      )
    }
    onChange(cohort?.cohortId ?? '')
    setOpen(false)
    setQuery('')
    setSearch('')
  }

  const optionIds = [
    ...selectableRows.map((row) => row.cohortId),
    ...(clearable ? [CLEAR_VALUE] : []),
  ]

  function selectByValue(candidate: string): void {
    if (candidate === CLEAR_VALUE) {
      choose(null)
      return
    }
    const cohort = rows.find((row) => row.cohortId === candidate)
    if (cohort && readinessProblems(cohort).length === 0) choose(cohort)
  }

  const placeholder = emptyLabel ?? '— select a cohort —'
  const triggerLabel =
    value === ''
      ? placeholder
      : selected.data
        ? describe(selected.data)
        : // Unresolved: the caller's own name for it if there is one — a
          // real membership must never read as "no cohort" just because a
          // lookup is in flight or failed.
          (fallbackLabel ??
            (selected.isPending ? 'Loading cohort…' : 'Cohort unavailable'))

  const showClear = clearable && value !== '' && !disabled

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative flex w-full items-center">
        <PopoverTrigger
          id={id}
          role="combobox"
          disabled={disabled || clientId === ''}
          aria-label={label}
          aria-required={required || undefined}
          aria-invalid={ariaInvalid || undefined}
          aria-describedby={ariaDescribedby}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault()
              setOpen(true)
            }
          }}
          className={cn(
            selectTriggerVariants({ size }),
            showClear ? 'pr-14' : 'pr-9',
          )}
        >
          <span className={value === '' ? 'text-text-faint' : undefined}>
            {triggerLabel}
          </span>
        </PopoverTrigger>
        {showClear && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Clear ${label.toLowerCase()}`}
            title={`Clear ${label.toLowerCase()}`}
            onClick={() => choose(null)}
            className="absolute right-7 size-6 text-text-secondary hover:text-text-primary"
          >
            <Icon name="clear" size={14} />
          </Button>
        )}
        <Icon
          name="choose"
          className="pointer-events-none absolute right-3 opacity-60"
        />
      </div>
      <PopoverContent className="w-(--anchor-width) p-0">
        <div className="border-b border-border-default">
          <ListboxSearch
            listId={listId}
            label={`Search ${label.toLowerCase()}`}
            placeholder="Search cohort name..."
            value={query}
            onValueChange={setQuery}
            activeId={active}
            optionIds={optionIds}
            onActiveIdChange={setActive}
            onSelect={selectByValue}
            onDismiss={() => setOpen(false)}
          />
        </div>
        <Listbox id={listId} label={label}>
          {results.isError && (
            <li role="presentation" className="px-3 py-4 text-center">
              <p role="alert" className="text-body text-danger-text">
                Could not load cohorts: {messageForCohortError(results.error)}
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-2"
                onClick={() => void results.refetch()}
              >
                Try again
              </Button>
            </li>
          )}

          {!results.isError && results.isPending && (
            <ListboxMessage>Searching cohorts…</ListboxMessage>
          )}

          {!results.isError && results.isSuccess && rows.length === 0 && (
            <ListboxMessage>
              {search !== ''
                ? `No cohorts match “${search}”.`
                : joinableOnly
                  ? // NOT "no cohorts": the filter is on, so this client may
                    // have plenty and all of them ended. Creating another is
                    // one repair; extending one is the other.
                    'No cohort here can take a wave. Extend a cohort’s access end, or create one.'
                  : 'No cohorts in this client yet.'}
            </ListboxMessage>
          )}

          {rows.map((cohort) => {
            const problems = readinessProblems(cohort)
            const incomplete = problems.length > 0
            return (
              <ListboxOption
                key={cohort.cohortId}
                listId={listId}
                value={cohort.cohortId}
                active={active === cohort.cohortId}
                onSelect={() => choose(cohort)}
                onHover={() => setActive(cohort.cohortId)}
                disabled={incomplete}
              >
                <Icon
                  name="selected"
                  className={cohort.cohortId === value ? 'opacity-100' : 'opacity-0'}
                />
                <span>
                  {describe(cohort)}
                  {incomplete && (
                    <span className="block text-caption text-danger-text">
                      Needs attention — {problems.join(' ')}
                    </span>
                  )}
                </span>
              </ListboxOption>
            )
          })}

          {results.isSuccess && total > rows.length && (
            <ListboxMessage className="text-caption">
              Showing {rows.length} of {total} — keep typing to narrow.
            </ListboxMessage>
          )}

          {clearable && (
            <ListboxOption
              listId={listId}
              value={CLEAR_VALUE}
              active={active === CLEAR_VALUE}
              onSelect={() => choose(null)}
              onHover={() => setActive(CLEAR_VALUE)}
            >
              <Icon
                name="selected"
                className={value === '' ? 'opacity-100' : 'opacity-0'}
              />
              {emptyLabel}
            </ListboxOption>
          )}
        </Listbox>
      </PopoverContent>
    </Popover>
  )
}
